/**
 * Custom Hook: useSocketOt
 * Spec: 003-optimized-ot-list
 * 
 * Hook para gestionar eventos Socket.io específicos de OTs
 * con patrón singleton para evitar múltiples conexiones
 */

import { useEffect, useRef } from 'react';
import { getSocket } from '../libs/http/initWebsocket';

/**
 * Hook para escuchar eventos de actualización de OTs vía Socket.io
 * @param {Function} onOtUpdate - Callback que se ejecuta cuando hay una actualización de OT
 * @returns {Object} { isConnected }
 */
export const useSocketOt = (onOtUpdate) => {
  const callbackRef = useRef(onOtUpdate);
  
  // Actualizar ref cuando cambia el callback (evita recrear listeners)
  useEffect(() => {
    callbackRef.current = onOtUpdate;
  }, [onOtUpdate]);

  useEffect(() => {
    // Obtener instancia singleton del socket
    const socket = getSocket();
    
    if (!socket) {
      console.warn('⚠️ useSocketOt: Socket no inicializado');
      return;
    }

    // Handler para evento de notificación
    const handleNotification = (notification) => {
      // Filtrar solo notificaciones de tipo 'ot-update'
      if (notification?.metadata?.tipo === 'ot-update') {
        console.log('📡 useSocketOt: Actualización de OT recibida', notification.metadata);
        
        if (callbackRef.current) {
          callbackRef.current({
            otId: notification.metadata.otId,
            numeroOrden: notification.metadata.numeroOrden,
            accion: notification.metadata.accion
          });
        }
      }
    };

    // Suscribirse al evento
    socket.on('nuevaNotificacion', handleNotification);
    
    console.log('✅ useSocketOt: Listener registrado para nuevaNotificacion');

    // Cleanup: remover listener al desmontar
    return () => {
      socket.off('nuevaNotificacion', handleNotification);
      console.log('🔴 useSocketOt: Listener removido');
    };
  }, []); // Solo se ejecuta al montar/desmontar

  // Retornar estado de conexión
  const socket = getSocket();
  return {
    isConnected: socket?.connected || false
  };
};
